import { z } from "zod";
import { api } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/models/crawler_ops.py — keep in sync.

   One unified capture mechanism, two modes:
     - login    fresh login, new cookie jar (replaces the stored session)
     - navigate augment an existing session (backup tool for missed bits)
   ────────────────────────────────────────────────────────────────────── */

export const CookieSummaryEntry = z.object({
  name:           z.string(),
  domain:         z.string(),
  expires_iso:    z.string().nullable().optional(),
  session_cookie: z.boolean().optional(),
});
export type CookieSummaryEntry = z.infer<typeof CookieSummaryEntry>;

export const CookieSummary = z.object({
  total:                z.number().int(),
  expired:              z.number().int(),
  session_cookies:      z.number().int(),
  valid_future:         z.number().int(),
  min_auth_expiry_iso:  z.string().nullable().optional(),
  soonest_auth_cookies: z.array(CookieSummaryEntry).default([]),
  auth_session_cookies: z.array(CookieSummaryEntry).default([]),
});
export type CookieSummary = z.infer<typeof CookieSummary>;

export const LiftedChannel = z.object({
  channel_id:   z.number().int(),
  channel_name: z.string(),
});
export type LiftedChannel = z.infer<typeof LiftedChannel>;

export const AuthProfileRow = z.object({
  channel_id:        z.number().int(),
  channel_name:      z.string(),
  label:             z.string(),
  auth_type:         z.string(),
  login_url:         z.string().nullable(),
  last_login_at:     z.string().nullable(),
  last_validated_at: z.string().nullable(),
  cookies_expire_at: z.string().nullable(),
  last_login_status: z.string(),
  failure_count:     z.number().int(),
  has_cookies:       z.boolean(),
  has_storage_state: z.boolean(),
  has_credentials:   z.boolean(),
  logged_in_capture: z.boolean().optional().default(false),
  cookie_summary:    CookieSummary.nullable().optional(),
  lifts:             z.array(LiftedChannel).default([]),
});
export type AuthProfileRow = z.infer<typeof AuthProfileRow>;

export const AuthProfileList = z.object({
  rows: z.array(AuthProfileRow),
});
export type AuthProfileList = z.infer<typeof AuthProfileList>;

export const SESSION_STATUS = [
  "idle", "starting", "x_ready", "vnc_ready", "novnc_ready", "browser_ready",
  "waiting_for_login", "waiting_for_navigate", "capturing",
  "done", "failed", "cancelled", "timeout",
] as const;
export type SessionPhase = (typeof SESSION_STATUS)[number];

export const SessionMode = z.enum(["login", "navigate"]);
export type SessionMode = z.infer<typeof SessionMode>;

export const SessionStatus = z.object({
  status:                  z.enum(SESSION_STATUS),
  mode:                    SessionMode.nullable().optional(),
  message:                 z.string().nullable().optional(),
  session_id:              z.string().nullable().optional(),
  channel_id:              z.number().int().nullable().optional(),
  channel_name:            z.string().nullable().optional(),
  login_url:               z.string().nullable().optional(),
  start_url:               z.string().nullable().optional(),
  novnc_url:               z.string().nullable().optional(),
  cookies:                 z.number().int().nullable().optional(),
  cookies_expire_at:       z.string().nullable().optional(),
  captured:                z.number().int().nullable().optional(),
  network_captured:        z.number().int().nullable().optional(),
  session_storage_origins: z.number().int().nullable().optional(),
  auth_domains:            z.number().int().nullable().optional(),
  started_at:              z.string().nullable().optional(),
  updated_at:              z.string().nullable().optional(),
});
export type SessionStatus = z.infer<typeof SessionStatus>;

export const SessionStartResponse = z.object({
  session_id: z.string(),
  channel_id: z.number().int(),
  mode:       SessionMode,
  novnc_url:  z.string().nullable(),
  status:     SessionStatus,
});
export type SessionStartResponse = z.infer<typeof SessionStartResponse>;

export const CredentialsResponse = z.object({
  channel_id:      z.number().int(),
  has_credentials: z.boolean(),
  username:        z.string().nullable().optional(),
});
export type CredentialsResponse = z.infer<typeof CredentialsResponse>;

export const ValidateResponse = z.object({
  channel_id:        z.number().int(),
  ok:                z.boolean(),
  http_status:       z.number().int().nullable(),
  elapsed_ms:        z.number().int(),
  error:             z.string().nullable().optional(),
  cookies_expire_at: z.string().nullable().optional(),
  last_validated_at: z.string().nullable().optional(),
});
export type ValidateResponse = z.infer<typeof ValidateResponse>;


/* ─── Calls ───────────────────────────────────────────────────────── */

export async function fetchAuthProfiles(): Promise<AuthProfileList> {
  const r = await api.get("/api/crawler/auth");
  return AuthProfileList.parse(r.data);
}

/** Start a capture session. mode=login (fresh) | navigate (augment). */
export async function startSession(
  channelId: number, mode: SessionMode,
): Promise<SessionStartResponse> {
  const r = await api.post(`/api/crawler/auth/${channelId}/session`, null, {
    params: { mode },
  });
  return SessionStartResponse.parse(r.data);
}

export async function pollSessionStatus(channelId: number): Promise<SessionStatus> {
  const r = await api.get(`/api/crawler/auth/${channelId}/session`);
  return SessionStatus.parse(r.data);
}

/** Login mode: operator finished logging in → flows into the navigate phase. */
export async function signalLoggedIn(channelId: number): Promise<SessionStatus> {
  const r = await api.post(`/api/crawler/auth/${channelId}/session/loggedin`);
  return SessionStatus.parse(r.data);
}

/** "Finish & capture" — triggers the exhaustive capture + DB write. */
export async function signalSessionDone(channelId: number): Promise<SessionStatus> {
  const r = await api.post(`/api/crawler/auth/${channelId}/session/done`);
  return SessionStatus.parse(r.data);
}

export async function signalSessionCancel(channelId: number): Promise<SessionStatus> {
  const r = await api.post(`/api/crawler/auth/${channelId}/session/cancel`);
  return SessionStatus.parse(r.data);
}

/** Full capture artifact: cookies, sessionStorage, network trace, auth map. */
export async function getSessionCapture(channelId: number): Promise<unknown> {
  const r = await api.get(`/api/crawler/auth/${channelId}/session/capture`);
  return r.data;
}

/** Store the publisher username/password (plaintext, never sent to np92). */
export async function setCredentials(
  channelId: number, body: { username?: string; password?: string },
): Promise<CredentialsResponse> {
  const r = await api.patch(`/api/crawler/auth/${channelId}/credentials`, body);
  return CredentialsResponse.parse(r.data);
}

export async function validateSession(channelId: number): Promise<ValidateResponse> {
  const r = await api.post(`/api/crawler/auth/${channelId}/validate`);
  return ValidateResponse.parse(r.data);
}


/* ──────────────────────────────────────────────────────────────────────
   Publishers page — full-catalog channel directory with health signals.
   Mirrors api/models/crawler_ops.py ChannelCard / CategoriesResponse.
   ────────────────────────────────────────────────────────────────────── */

export const ChannelCard = z.object({
  channel_id:                  z.number().int(),
  channel_name:                z.string(),
  language:                    z.string().nullable(),
  category:                    z.string(),
  editions_total:              z.number().int(),
  editions_active:             z.number().int(),
  onboarding_state:            z.string(),                  // crawling / auth_ready / auth_partial / catalog
  requires_auth:               z.boolean(),
  auth_profile_id:             z.number().int().nullable(),
  login_url:                   z.string().nullable(),
  auth_status:                 z.string().nullable(),
  auth_cookies_expire_in_days: z.number().int().nullable(),
  auth_last_validated_at:      z.string().nullable(),
  has_session_state:           z.boolean(),
  today_issues_ok:             z.number().int(),
  today_pages:                 z.number().int(),
  median_pages_7d:             z.number().int().nullable(),
  today_delta_pct:             z.number().int().nullable(),
  last_ok_business_date:       z.string().nullable(),
  days_since_last_ok:          z.number().int().nullable(),
  no_publish_today:            z.boolean(),
  reverify_status:             z.string().nullable(),
  reverify_at:                 z.string().nullable(),
});
export type ChannelCard = z.infer<typeof ChannelCard>;

export const CategoryCount = z.object({
  category: z.string(),
  total:    z.number().int(),
});
export type CategoryCount = z.infer<typeof CategoryCount>;

export const PendingAction = z.object({
  kind:       z.string(),
  channel_id: z.number().int(),
  channel:    z.string(),
  detail:     z.string(),
});
export type PendingAction = z.infer<typeof PendingAction>;

export const CategoriesResponse = z.object({
  business_date:      z.string(),
  page:               z.number().int(),
  page_size:          z.number().int(),
  total_filtered:     z.number().int(),
  counts_by_category: z.array(CategoryCount),
  channels:           z.array(ChannelCard),
  pending:            z.array(PendingAction),
});
export type CategoriesResponse = z.infer<typeof CategoriesResponse>;

export interface CategoriesQuery {
  business_date?: string;
  category?:      string;
  state?:         string;
  q?:             string;
  page?:          number;
  page_size?:     number;
}

export async function fetchCrawlerCategories(opts: CategoriesQuery = {}): Promise<CategoriesResponse> {
  const r = await api.get("/api/crawler/categories", { params: opts });
  return CategoriesResponse.parse(r.data);
}

/* Create an AuthProfile for a channel that doesn't yet have one. */
export const CreateAuthProfileResponse = z.object({
  auth_profile_id: z.number().int(),
  channel_id:      z.number().int(),
  channel_name:    z.string(),
  login_url:       z.string(),
  created:         z.boolean(),
});
export type CreateAuthProfileResponse = z.infer<typeof CreateAuthProfileResponse>;

export async function createAuthProfile(
  channel_id: number, login_url: string, label?: string,
): Promise<CreateAuthProfileResponse> {
  const r = await api.post("/api/crawler/auth", { channel_id, login_url, label });
  return CreateAuthProfileResponse.parse(r.data);
}


/* ────────────────────────────────────────────────────────────────────
   Channel Master (Phase 3 of Channel_Crawler refactor, 2026-05-26)
   ──────────────────────────────────────────────────────────────────── */

export const ChannelLift = z.object({
  channel_id:   z.number().int(),
  channel_name: z.string(),
});
export type ChannelLift = z.infer<typeof ChannelLift>;

export const AuthProfileOption = z.object({
  id:            z.number().int(),
  label:         z.string(),
  channel_id:    z.number().int().nullable().optional(),
  channel_name:  z.string().nullable().optional(),
  has_session:   z.boolean(),
  lift_count:    z.number().int(),
});
export type AuthProfileOption = z.infer<typeof AuthProfileOption>;

export const ChannelMasterRow = z.object({
  channel_id:                z.number().int(),
  channel_name:              z.string(),
  category:                  z.string().nullable().optional(),
  language:                  z.string().nullable().optional(),
  needs_login:               z.boolean(),
  auth_profile_id:           z.number().int().nullable().optional(),
  auth_profile_label:        z.string().nullable().optional(),
  auth_profile_has_session:  z.boolean().default(false),
  epaper_url:                z.string().nullable().optional(),
  strategy_type:             z.string().nullable().optional(),
  parser_key:                z.string().nullable().optional(),
  manifest_url_template:     z.string().nullable().optional(),
  edition_count:             z.number().int().default(0),
  crawlable_edition_count:   z.number().int().default(0),
  cities:                    z.array(z.string()).default([]),
  needs_review_count:        z.number().int().default(0),
  response_kind:             z.string().nullable().optional(),
  normalizer_key:            z.string().nullable().optional(),
  page_url_template:         z.string().nullable().optional(),
  referer_header:            z.string().nullable().optional(),
  origin_header:             z.string().nullable().optional(),
  user_agent_override:       z.string().nullable().optional(),
  publication_code:          z.string().nullable().optional(),
  jpeg_quality:              z.number().int().nullable().optional(),
  timeout_seconds:           z.number().int().nullable().optional(),
});
export type ChannelMasterRow = z.infer<typeof ChannelMasterRow>;

export const ChannelMasterContext = z.object({
  channels:              z.array(ChannelMasterRow),
  auth_profile_options:  z.array(AuthProfileOption),
});
export type ChannelMasterContext = z.infer<typeof ChannelMasterContext>;

export type ChannelMasterPatch = Partial<{
  needs_login: boolean;
  logged_in_capture: boolean;
  auth_profile_id: number | null;
  explicit_unset_auth_profile: boolean;
  epaper_url: string | null;
  strategy_type: string | null;
  parser_key: string | null;
  manifest_url_template: string | null;
  page_url_template: string | null;
  response_kind: string | null;
  normalizer_key: string | null;
  referer_header: string | null;
  origin_header: string | null;
  user_agent_override: string | null;
  publication_code: string | null;
  jpeg_quality: number | null;
  timeout_seconds: number | null;
}>;

export const ChannelMasterPatchResponse = z.object({
  ok:              z.boolean(),
  channel_id:      z.number().int(),
  updated_fields:  z.array(z.string()),
  row:             ChannelMasterRow,
});
export type ChannelMasterPatchResponse = z.infer<typeof ChannelMasterPatchResponse>;

export async function fetchChannelMaster(): Promise<ChannelMasterContext> {
  const r = await api.get("/api/crawler/channels");
  return ChannelMasterContext.parse(r.data);
}

export async function patchChannel(
  channelId: number,
  patch: ChannelMasterPatch,
): Promise<ChannelMasterPatchResponse> {
  const r = await api.patch(`/api/crawler/channels/${channelId}`, patch);
  return ChannelMasterPatchResponse.parse(r.data);
}
